import React from 'react'
import {MemberJoinInfo} from '../../../Props/MembersJoinPageStates'
import {Signboard} from '../../../DTO/SignboardDTO'
import {useLocation} from 'react-router-dom'
import {MBMemberError} from '../../../Common/MBMemberError'
import {ContractInfoWithContractDate, ContractMethod} from '../../../Props/ContractInputInfo'
import {ContractInformationContentBlock} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractInformationContentBlock'
import {RegisteredMemberInfoContents} from '../../Components/CompleteContentBlocks/RegisteredMemberInfoContents'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ExternalLinkButton} from '../../Atoms/Buttons'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {Spacer} from '../../../../Common/Components/Spacer'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {CvsPaymentInfoContentBlock} from '../../Components/PaymentsRelations/ConfirmContentBlocks/CvsPaymentInfoContentBlock'
import {CvsContract} from '../../../DTO/CvsContractDTO'
import {MembersRegisteredPageFreeBucketLocationState} from '../Signup/MembersRegisteredPage'
import {BucketMemberId} from '../../../DTO/BucketMemberId'
import {RegisteredOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisteredOrganizationContentBlock'
import {RegisteredFreeServiceContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisteredFreeServiceContentBlock'
import {useTranslation} from 'react-i18next'

type LocationStateCommon = {
  contractMethod?: ContractMethod
  memberInfo: MemberJoinInfo
  signboard: Signboard
  contractInfoForCompletePage: ContractInfoWithContractDate
  bucketMemberIdList: BucketMemberId[]
}

export type JoinCompletePageLocationState = LocationStateCommon

export type JoinCompletePageCvsLocationState = LocationStateCommon & {
  contractMethod: 'convenience_store'
  cvsContract: CvsContract
}

export type JoinCompletePageFreeBucketLocationState =
  Omit<LocationStateCommon, 'contractInfoForCompletePage'> & {
  contractMethod: 'none'
}

const isLocationStateCvs = (state: JoinCompletePageLocationState |
  JoinCompletePageCvsLocationState |
  MembersRegisteredPageFreeBucketLocationState,
)
  : state is JoinCompletePageCvsLocationState => state.contractMethod === 'convenience_store'

export const MembersJoinCompletePage: React.VFC = () => {
  const locationState =
    useLocation<JoinCompletePageLocationState |
      JoinCompletePageCvsLocationState |
      JoinCompletePageFreeBucketLocationState>().state

  const {t} = useTranslation('member')

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  const {memberInfo, signboard, contractMethod, bucketMemberIdList} = locationState
  const {mail, familyName, firstName} = memberInfo
  const isCvs = isLocationStateCvs(locationState)

  return (
    <MembersPageTemplate>
      <PageTitle>{`${signboard.signupName} 新規入会完了`}</PageTitle>

      <StepGuide activity={'signup'} current={'complete'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <RegisteredOrganizationContentBlock
            signboard={signboard}
            title={isCvs ? t('新規入会完了.入会バケット情報.見出し コンビニ') : t('新規入会完了.入会バケット情報.見出し')}
            bucketMemberIdList={bucketMemberIdList}
            isCvsPayment={isCvs}
          />
        </PIMStack>
      </Spacer>

      {((): JSX.Element => {
        if (contractMethod === 'none') {
          return <></>
        }
        if (isCvs) {
          return <CvsPaymentInfoContentBlock
            title={'お支払い情報'}
            contractInfoWithContractDate={locationState.contractInfoForCompletePage}
            dataTestId={'block-payment-info'}
            cvsContract={locationState.cvsContract}
            signboard={signboard}
          />
        }
        return <ContractInformationContentBlock
          title={'契約情報'}
          contractInfoWithContractDate={locationState.contractInfoForCompletePage}
          dataTestId={'block-contract-info'}
        />
      })()}

      {isCvs && (
        <RegisteredFreeServiceContentBlock
          freeServices={signboard.freeServices}
          bucketMemberIdList={bucketMemberIdList}
        />
      )}

      <RegisteredMemberInfoContents
        title={t('新規入会完了.登録情報.見出し')}
        topMessage={[t('新規入会完了.登録情報.説明'), `${signboard.signupName}の新規入会手続きが完了しました。`]}
        mail={mail}
        familyName={familyName}
        firstName={firstName}
      />

      <Spacer mt={'30px'}>
        <PIMPageActions>
          <ExternalLinkButton
            label={`${signboard.signupReturnPageInfo.name}に移動する`}
            url={signboard.signupReturnPageInfo.url}
          />
        </PIMPageActions>
      </Spacer>
    </MembersPageTemplate>
  )
}

const isInvalidLocationState = (
  locationState: JoinCompletePageLocationState |
    JoinCompletePageCvsLocationState |
    JoinCompletePageFreeBucketLocationState,
): boolean => {
  if (!locationState) {
    return true
  }
  const isCvs = isLocationStateCvs(locationState)

  if (!locationState.memberInfo) {
    return true
  }
  if (!locationState.signboard) {
    return true
  }
  if (!locationState.signboard.signupName ||
    !locationState.signboard.mainService ||
    !locationState.signboard.freeServices ||
    !locationState.signboard.signupReturnPageInfo ||
    !locationState.signboard.logoImgUrl
  ) {
    return true
  }

  if (isCvs) {
    if (!locationState.cvsContract) return true
  }

  if (locationState.contractMethod === 'none') {
    return false
  }

  if (!locationState.contractInfoForCompletePage) {
    return true
  }

  if (
    !locationState.contractInfoForCompletePage.term ||
    !locationState.contractInfoForCompletePage.amount ||
    !locationState.contractInfoForCompletePage.method ||
    !locationState.contractInfoForCompletePage.contractDate
  ) {
    return true
  }

  return false
}
